/**
 * Heat Loss Calculator
 * ===
 * Range Selection View
 * 
 * Extends Backbone View.
 *
 * Author: Duncan McMillan
 *
 * Copyright (c) August 2024 · Art & Soul Communications Ltd.
 * https://artandsoul.co.uk
 *
 */
'use strict';

import _ from 'underscore';
import Backbone from 'backbone';

import { HeatLossCalculator } from './../HeatLossCalculator.Object.js';
import { HLC_Utils } from './../HeatLossCalculator.Utils.js';
import { HLC_Configs } from './../HeatLossCalculator.Configs.js';

import { RangeModel } from './../backbone.models/RangeModel.js';

/**
 * Range Selection View
 *
 */
export var RangeSelectionView = Backbone.View.extend({
    
    events: {
        'click .hlc-button--select-all-ranges' : 'selectAllRanges',
        'click .hlc-button--deselect-all-ranges' : 'deselectAllRanges',
        
        'click .hlc-button--select-range' : 'toggleRangeSelection',
        
        'click .hlc-checkbox[name="radiator_series"]' : 'toggleSeriesSelection'
    },
    
    tagName: 'section',
    className: 'hlc-section hlc-radiator-ranges-pane is-inactive',
    
    template: undefined,
    
    collection: undefined, // Radiator ranges
    
    scrollto_offset: undefined,
    
    initialize: function(options) {
        
        this.template = _.template(options.template);
        
        if (options.scrollto_offset) {
            this.scrollto_offset = options.scrollto_offset;
        } else {
            this.scrollto_offset = 0;
        }
        
        return this;
    },
    
    activate: function() {
        
        var that = this;
        
        this.$el.find('.hlc-radiator-ranges').slideDown({
            start: function () {
                $(this).css({
                    display: 'flex'
                });
            }
        });
        this.$el.removeClass('is-inactive').addClass('is-active');
    },
    
    render: function() {
        
        var data, compiled, appropriate_ranges, appropriate_series;
        
        appropriate_ranges = new Backbone.Collection();
        
        if (this.model.get('heating_system_type') === 'electric') {
            appropriate_ranges.add(this.collection.filter(function(range) {
                return range.get('classification').name.toLowerCase() === 'electric';
            }));
        } else {
            appropriate_ranges.add(this.collection.filter(function(range) {
                return range.get('classification').name.toLowerCase() !== 'electric';
            }));
        }
        
        // Stringify our series objects and use them to populate a Set to eliminate any duplicates…
        var strung_series = new Set( appropriate_ranges.pluck('series').map( series_obj => JSON.stringify(series_obj) ) );
        
        // …then rehydrate back into objects and sort while we’re at it
        appropriate_series = _.sortBy( Array.from(strung_series).map(series_str => JSON.parse(series_str)), 'position' );
        
        data = this.model.toJSON();
        
        data.all_ranges = appropriate_ranges.toJSON();
        data.preferred_range_ids = HeatLossCalculator.app.preferred_ranges.pluck('id');
        
        data.appropriate_series = appropriate_series;
        data.user_can_select_series = HLC_Configs.hlc_version === 'commercial' && this.model.get('heating_system_type') !== 'electric';
        
        this.$el.html(this.template(data));
        
        return this;
    },
    
    toggleSeriesSelection: function(e) {
        
        var series_id, is_checked;
        
        series_id = $(e.currentTarget).val();
        is_checked = $(e.currentTarget).is(':checked');
        
        if (is_checked) {
            this.$el.find('.hlc-radiator-range-series-' + series_id).fadeIn();
        } else {
            this.$el.find('.hlc-radiator-range-series-' + series_id).fadeOut();
        }
    },
    
    toggleRangeSelection: function(e) {
        
        e.preventDefault();
        
        var range_id = $(e.currentTarget).attr('value');
        
        if ($(e.currentTarget).hasClass('is-selected')) {
            $(e.currentTarget).removeClass('is-selected');
            HeatLossCalculator.vent.trigger('rangeDeselect', this.collection.get(range_id));
        } else {
            $(e.currentTarget).addClass('is-selected');
            HeatLossCalculator.vent.trigger('rangeSelect', this.collection.get(range_id));
        }
        
        return this;
    },
    
    selectAllRanges: function(e) {
        
        e.preventDefault();
        
        var that, selected_ranges;
        
        that = this;
        selected_ranges = new Backbone.Collection();
        
        // Some ranges may be hidden because they have been filtered by series
        this.$el.find('.hlc-button--select-range').filter(function(i) {
            
            if ($(this).parent().css('display') !== 'none') {
                selected_ranges.add(that.collection.get($(this).val()));
                return true;
            }
            
            return false;
            
        }).addClass('is-selected');
        
        HeatLossCalculator.vent.trigger('allRangesSelect', selected_ranges);
        
        return this;
    },
    
    deselectAllRanges: function(e) {
        
        e.preventDefault();
        
        this.$el.find('.hlc-button--select-range').removeClass('is-selected');
        HeatLossCalculator.vent.trigger('allRangesDeselect');
        
        return this;
    }
    
});
